import { formatAmount } from '@/lib/utils';
import { fromLocalDateStr } from '@/lib/date';
import { ADDED_BY_YOU_SECTION, type ShoppingListItem } from '@/services/shoppingLists';

/**
 * Pure helpers for the shopping list page (#330): how an amount reads, how a
 * typed amount splits into number and unit, how rows group into shop sections
 * and how a list's dates read.
 */

/** Short forms for the long unit names recipes store. Anything else shows as is. */
const SHORT_UNITS: Record<string, string> = {
    gram: 'g',
    grams: 'g',
    kilogram: 'kg',
    kilograms: 'kg',
    milliliter: 'ml',
    milliliters: 'ml',
    millilitre: 'ml',
    millilitres: 'ml',
    liter: 'l',
    liters: 'l',
    litre: 'l',
    litres: 'l',
    tablespoon: 'Tbsp',
    tablespoons: 'Tbsp',
    tbsp: 'Tbsp',
    teaspoon: 'tsp',
    teaspoons: 'tsp',
};

export function displayUnit(unit: string | null | undefined): string {
    const trimmed = (unit ?? '').trim();
    return SHORT_UNITS[trimmed.toLowerCase()] ?? trimmed;
}

/**
 * "500 g", "2 packs", "a few" or "" for no quantity. A free-text row can have
 * no amount at all, and then only its unit text (if any) is shown.
 */
export function formatItemAmount(item: Pick<ShoppingListItem, 'amount' | 'unit'>): string {
    const unit = displayUnit(item.unit);
    if (item.amount === null || item.amount === undefined) return unit;
    return `${formatAmount(item.amount)} ${unit}`.trim();
}

// Friendlier amounts (#330, owner's answer 7): round up to what you'd buy and
// still show what the recipes use. Display only: the stored amount stays exact,
// because later menu merges add to it.

/** Units you buy whole. These show the count with no unit ("3 Bell Pepper"). */
const BARE_COUNT_UNITS = new Set([
    '',
    'piece',
    'pieces',
    'pc',
    'pcs',
    'whole',
    'each',
    'egg',
    'eggs',
]);

/** Units you buy whole that read better with the unit kept ("3 cloves Garlic"). */
const COUNT_UNIT_PLURALS: Record<string, string> = {
    clove: 'cloves',
    bunch: 'bunches',
    tin: 'tins',
    can: 'cans',
    slice: 'slices',
    head: 'heads',
    stalk: 'stalks',
    sprig: 'sprigs',
    leaf: 'leaves',
    bag: 'bags',
    packet: 'packets',
};
/** Every count unit with a plural, including the ones the list hides. */
const UNIT_PLURALS: Record<string, string> = {
    ...COUNT_UNIT_PLURALS,
    piece: 'pieces',
    egg: 'eggs',
};
const UNIT_SINGULARS: Record<string, string> = Object.fromEntries(
    Object.entries(UNIT_PLURALS).map(([one, many]) => [many, one]),
);

/** Grams or millilitres per stored unit, keyed by lower-case unit. */
const METRIC_UNITS: Record<string, { kind: 'mass' | 'volume'; factor: number }> = {
    g: { kind: 'mass', factor: 1 },
    gram: { kind: 'mass', factor: 1 },
    grams: { kind: 'mass', factor: 1 },
    kg: { kind: 'mass', factor: 1000 },
    kilogram: { kind: 'mass', factor: 1000 },
    kilograms: { kind: 'mass', factor: 1000 },
    ml: { kind: 'volume', factor: 1 },
    milliliter: { kind: 'volume', factor: 1 },
    milliliters: { kind: 'volume', factor: 1 },
    millilitre: { kind: 'volume', factor: 1 },
    millilitres: { kind: 'volume', factor: 1 },
    l: { kind: 'volume', factor: 1000 },
    liter: { kind: 'volume', factor: 1000 },
    liters: { kind: 'volume', factor: 1000 },
    litre: { kind: 'volume', factor: 1000 },
    litres: { kind: 'volume', factor: 1000 },
};

const SPOON_UNITS = new Set(['tsp', 'teaspoon', 'teaspoons', 'tbsp', 'tablespoon', 'tablespoons']);
const CUP_UNITS = new Set(['cup', 'cups']);

/** Below this relative difference the "used" note is noise. */
const USED_NOTE_THRESHOLD = 0.02;
/** Float slack so 3.0000001 still counts as 3. */
const EPSILON = 1e-6;

/** Round up to a multiple of `step`, ignoring float dust. */
function ceilTo(value: number, step: number): number {
    return Math.ceil(value / step - EPSILON) * step;
}

/** Plain number, at most `decimals` decimals, no trailing zeros. */
function trimNumber(value: number, decimals = 2): string {
    return parseFloat(value.toFixed(decimals)).toString();
}

const isWhole = (value: number) => Math.abs(value - Math.round(value)) < 0.005;

/** 133.33 g reads "133 g", 2.5 g "2.5 g", 1333 g "1.33 kg" (base is g or ml). */
function formatMetric(base: number, kind: 'mass' | 'volume'): string {
    const [small, big] = kind === 'mass' ? ['g', 'kg'] : ['ml', 'l'];
    if (base >= 1000 - EPSILON) return `${trimNumber(base / 1000)} ${big}`;
    return `${trimNumber(base, base < 10 ? 1 : 0)} ${small}`;
}

/** What to buy, rounded up: 5 g steps under 100, 10 g to 1 kg, then 0.1 kg. */
function roundMetricUp(base: number): number {
    if (base < 100) return ceilTo(base, 5);
    if (base < 1000) return ceilTo(base, 10);
    return ceilTo(base, 100);
}

const NICE_FRACTIONS: Array<[number, string]> = [
    [1 / 8, '1/8'],
    [1 / 4, '1/4'],
    [1 / 3, '1/3'],
    [1 / 2, '1/2'],
    [2 / 3, '2/3'],
    [3 / 4, '3/4'],
];

/** 0.5 is "1/2", 0.125 is "1/8", 1.333 is "1 1/3", 2 is "2", 2.16 is "2.2" (one decimal otherwise). */
export function formatSpoonAmount(value: number): string {
    const whole = Math.floor(value + EPSILON);
    const rest = value - whole;
    if (rest < 0.02) return String(whole);
    if (rest > 0.98) return String(whole + 1);
    const nice = NICE_FRACTIONS.find(([fraction]) => Math.abs(rest - fraction) < 0.02);
    if (nice) return whole ? `${whole} ${nice[1]}` : nice[1];
    const decimal = trimNumber(value, 1);
    // 0.04 would read "0"; keep two decimals for something that small.
    return decimal === '0' ? trimNumber(value, 2) : decimal;
}

/** "1 1/2 Tbsp" or "2/3 cup" for spoons and cups, null for any other unit. */
function formatMeasure(amount: number, unit: string): string | null {
    const key = unit.trim().toLowerCase();
    if (SPOON_UNITS.has(key)) return `${formatSpoonAmount(amount)} ${displayUnit(unit)}`;
    if (CUP_UNITS.has(key)) {
        return `${formatSpoonAmount(amount)} ${amount > 1 + 0.02 ? 'cups' : 'cup'}`;
    }
    return null;
}

export interface FriendlyAmount {
    /** The bold part: "3", "3 cloves", "140 g", "1 1/2 cups", "a few", "". */
    main: string;
    /** What the recipes use when the main amount was rounded: "2.83", "133 g". */
    used: string | null;
}

function countUnitWord(unit: string, amount: number): string {
    const key = unit.trim().toLowerCase();
    const one = UNIT_SINGULARS[key] ?? key;
    return amount > 1 + EPSILON ? (UNIT_PLURALS[one] ?? unit.trim()) : one;
}

/** True for a row whose amount the recipes worked out (not typed by the client). */
export function isRecipeAmount(
    item: Pick<ShoppingListItem, 'ingredientId' | 'source' | 'userEdited'>,
): boolean {
    return !!item.ingredientId && item.source !== 'MANUAL' && !item.userEdited;
}

/**
 * How a row's amount reads on the list. A recipe amount is rounded up to what
 * you would buy (counts to whole items, grams and millilitres to shop-sized
 * steps, spoons and cups trimmed to a simple fraction) with the exact amount
 * as a "used" note when rounding changed it noticeably. Free-text rows and
 * amounts the client set show exactly as entered.
 */
export function friendlyAmount(
    item: Pick<ShoppingListItem, 'amount' | 'unit' | 'ingredientId' | 'source' | 'userEdited'>,
): FriendlyAmount {
    const amount = item.amount;
    if (!isRecipeAmount(item) || amount === null || amount === undefined || !(amount > 0)) {
        return { main: formatItemAmount(item), used: null };
    }

    const key = (item.unit ?? '').trim().toLowerCase();

    if (BARE_COUNT_UNITS.has(key) || key in UNIT_PLURALS || key in UNIT_SINGULARS) {
        const buy = Math.max(1, Math.ceil(amount - 0.005));
        const unit = BARE_COUNT_UNITS.has(key) ? '' : countUnitWord(item.unit, buy);
        return {
            main: `${buy} ${unit}`.trim(),
            used: isWhole(amount) ? null : trimNumber(amount),
        };
    }

    const metric = METRIC_UNITS[key];
    if (metric) {
        const base = amount * metric.factor;
        const buy = roundMetricUp(base);
        const noticeable = (buy - base) / base >= USED_NOTE_THRESHOLD;
        const used = formatMetric(base, metric.kind);
        const main = formatMetric(buy, metric.kind);
        return { main, used: noticeable && used !== main ? used : null };
    }

    return { main: formatMeasure(amount, item.unit) ?? formatItemAmount(item), used: null };
}

/**
 * The exact amount the recipes use, trimmed for reading: "2.83 pieces",
 * "133 g", "1 1/3 Tbsp". Used by the editor's "Recipes use X" note.
 */
export function exactRecipeAmount(item: Pick<ShoppingListItem, 'amount' | 'unit'>): string {
    const amount = item.amount;
    if (amount === null || amount === undefined || !(amount > 0)) return formatItemAmount(item);
    const key = (item.unit ?? '').trim().toLowerCase();
    const metric = METRIC_UNITS[key];
    if (metric) return formatMetric(amount * metric.factor, metric.kind);
    const measure = formatMeasure(amount, item.unit);
    if (measure) return measure;
    if (key in UNIT_PLURALS || key in UNIT_SINGULARS) {
        return `${trimNumber(amount)} ${countUnitWord(item.unit, amount)}`;
    }
    return formatItemAmount(item);
}

const FRACTIONS: Record<string, number> = { '½': 0.5, '¼': 0.25, '¾': 0.75 };
const MAX_UNIT_LENGTH = 50;

export interface ParsedAmount {
    amount: number | null;
    unit: string;
    /** False when the text does not start with a number ("a few"). */
    numeric: boolean;
}

/**
 * Split what someone typed in the amount box into the backend's amount and
 * unit: "500 g" is 500 and "g", "1.5kg" is 1.5 and "kg", "1,5 kg" is 1.5 (a
 * decimal comma), "1/2 cup" is 0.5 and "cup", "2" is 2 and "". Text that does
 * not start with a number keeps the whole text as the unit with no amount, so
 * "a few" still reads "a few" on a free-text row.
 */
export function parseAmountInput(text: string): ParsedAmount {
    const trimmed = text.trim().replace(/\s+/g, ' ');
    if (!trimmed) return { amount: null, unit: '', numeric: true };

    const match = trimmed.match(/^(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?|[½¼¾])\s*(.*)$/);
    if (!match) {
        return { amount: null, unit: trimmed.slice(0, MAX_UNIT_LENGTH), numeric: false };
    }

    const [, number, rest] = match;
    let amount: number;
    if (number in FRACTIONS) {
        amount = FRACTIONS[number];
    } else if (number.includes('/')) {
        const [top, bottom] = number.split('/').map((part) => Number(part.trim()));
        if (!bottom)
            return { amount: null, unit: trimmed.slice(0, MAX_UNIT_LENGTH), numeric: false };
        amount = Math.round((top / bottom) * 1000) / 1000;
    } else {
        amount = Number(number.replace(',', '.'));
    }

    return { amount, unit: rest.trim().slice(0, MAX_UNIT_LENGTH), numeric: true };
}

/** One unit's comparable form: short ("g" for "gram"), lower case, singular. */
function unitKey(unit: string | null | undefined): string {
    const short = displayUnit(unit).toLowerCase();
    return UNIT_SINGULARS[short] ?? short;
}

/** "g" and "gram", "pieces" and "piece", "Tbsp" and "tablespoon" are one unit. */
export function sameUnit(a: string | null | undefined, b: string | null | undefined): boolean {
    return unitKey(a) === unitKey(b);
}

/**
 * The unit to save for what was typed in a row's amount box (#376).
 *
 * A bare number ("5") changes the amount only: the row keeps its unit, so a
 * "piece" row stays "piece" and still merges with the menu. Typing the stored
 * unit back in another form ("g" for "gram", "pieces" for "piece") keeps the
 * stored spelling for the same reason. Anything else is a new unit.
 */
export function unitForEdit(parsed: ParsedAmount, storedUnit: string | null | undefined): string {
    const stored = storedUnit ?? '';
    if (parsed.amount !== null && !parsed.unit) return stored;
    return sameUnit(parsed.unit, stored) ? stored : parsed.unit;
}

export interface ShoppingSection {
    section: string;
    items: ShoppingListItem[];
    ticked: number;
}

/**
 * Group rows by shop section. "Added by you" comes first; the other sections
 * keep the order the server sent (it sorts by section). Inside a section the
 * rows still to buy come first and ticked rows drop to the bottom, each part
 * keeping its server order.
 */
export function groupShoppingItems(items: ShoppingListItem[]): ShoppingSection[] {
    const bySection = new Map<string, ShoppingListItem[]>();
    for (const item of items) {
        const section = item.section?.trim() || (item.ingredient ? 'Other' : ADDED_BY_YOU_SECTION);
        const rows = bySection.get(section) ?? [];
        rows.push(item);
        bySection.set(section, rows);
    }

    const sections = [...bySection.entries()].map(([section, rows]) => ({
        section,
        items: [
            ...rows.filter((row) => !row.isPurchased),
            ...rows.filter((row) => row.isPurchased),
        ],
        ticked: rows.filter((row) => row.isPurchased).length,
    }));

    return [
        ...sections.filter((group) => group.section === ADDED_BY_YOU_SECTION),
        ...sections.filter((group) => group.section !== ADDED_BY_YOU_SECTION),
    ];
}

/** "2026-10-22T00:00:00.000Z" or "2026-10-22" to "2026-10-22". */
export function toDateOnly(value: string | null | undefined): string | null {
    if (!value) return null;
    const match = value.match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : null;
}

const day = (date: Date) => date.getDate();
const month = (date: Date) => date.toLocaleDateString('en-ZA', { month: 'short' });

/** "22 Oct", "22 to 28 Oct", "29 Oct to 4 Nov", "29 Dec 2026 to 4 Jan 2027". */
export function formatDateRange(start: string, end: string): string {
    const from = fromLocalDateStr(start);
    const to = fromLocalDateStr(end);
    if (start === end) return `${day(from)} ${month(from)}`;
    if (from.getFullYear() !== to.getFullYear()) {
        return `${day(from)} ${month(from)} ${from.getFullYear()} to ${day(to)} ${month(to)} ${to.getFullYear()}`;
    }
    if (from.getMonth() === to.getMonth()) return `${day(from)} to ${day(to)} ${month(to)}`;
    return `${day(from)} ${month(from)} to ${day(to)} ${month(to)}`;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function dayNumber(planStart: string, date: string): number {
    return (
        Math.round(
            (fromLocalDateStr(date).getTime() - fromLocalDateStr(planStart).getTime()) / MS_PER_DAY,
        ) + 1
    );
}

/**
 * The line under the list's title. "Days 1 to 7, 22 to 28 Oct" when the list
 * holds menu items and its dates sit inside the client's plan; otherwise just
 * the dates; null when the list has no dates (a hand-made list).
 */
export function formatListRange(
    list: { startDate?: string | null; endDate?: string | null; items: ShoppingListItem[] },
    plan?: { startDate: string; endDate: string } | null,
): string | null {
    const start = toDateOnly(list.startDate);
    const end = toDateOnly(list.endDate) ?? start;
    if (!start || !end) return null;

    const dates = formatDateRange(start, end);
    const fromPlan = list.items.some((item) => item.source === 'MENU');
    const planStart = toDateOnly(plan?.startDate);
    const planEnd = toDateOnly(plan?.endDate);
    if (!fromPlan || !planStart || !planEnd || start < planStart || end > planEnd) return dates;

    const first = dayNumber(planStart, start);
    const last = dayNumber(planStart, end);
    return first === last ? `Day ${first}, ${dates}` : `Days ${first} to ${last}, ${dates}`;
}

/** "Sat" for a YYYY-MM-DD date. */
export function shortWeekday(date: string): string {
    return fromLocalDateStr(date).toLocaleDateString('en-ZA', { weekday: 'short' });
}
